import React, { useState } from 'react';
import {
  Monitor, Server, Cloud, Building2, CheckCircle2, XCircle,
  ChevronDown, ChevronUp, Cpu, Globe
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const PlatformCard = ({ icon: Icon, title, subtitle, platforms, color, isExpanded, onToggle }) => {
  const colorClasses = {
    cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', badge: 'bg-cyan-500' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', badge: 'bg-purple-500' },
    green: { bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-400', badge: 'bg-green-500' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', badge: 'bg-blue-500' },
    orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400', badge: 'bg-orange-500' },
  };

  const colors = colorClasses[color] || colorClasses.cyan;

  return (
    <div className={`rounded-2xl ${colors.bg} border ${colors.border} overflow-hidden transition-all duration-300`}>
      <button
        onClick={onToggle}
        className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
          {isExpanded ? (
            <ChevronUp className={`w-5 h-5 ${colors.text}`} />
          ) : (
            <ChevronDown className={`w-5 h-5 ${colors.text}`} />
          )}
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-white font-medium">Platform</th>
                  <th className="text-left py-2 text-white font-medium">Version/Type</th>
                  <th className="text-center py-2 text-white font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {platforms.map((platform, index) => (
                  <tr key={index} className="border-b border-white/5">
                    <td className="py-2">{platform.name}</td>
                    <td className="py-2">{platform.version}</td>
                    <td className="py-2 text-center">
                      <CheckCircle2 className="w-4 h-4 text-green-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlatformSupport = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.1 });
  const [expandedCard, setExpandedCard] = useState(0);

  const platformCategories = [
    {
      icon: Server,
      title: 'Linux x86_64 (Intel/AMD)',
      subtitle: 'Primary HFT Platform - 15+ distributions',
      color: 'cyan',
      platforms: [
        { name: 'CentOS', version: '7, 8, 9' },
        { name: 'RHEL (Red Hat Enterprise Linux)', version: '7, 8, 9' },
        { name: 'Rocky Linux', version: '8, 9' },
        { name: 'AlmaLinux', version: '8, 9' },
        { name: 'Ubuntu', version: '18.04, 20.04, 22.04, 24.04' },
        { name: 'Debian', version: '10, 11, 12' },
        { name: 'Amazon Linux', version: '2, 2023' },
        { name: 'Fedora', version: '30+' },
        { name: 'SUSE Linux Enterprise', version: '15+' },
        { name: 'Oracle Linux', version: '7, 8, 9' },
      ],
    },
    {
      icon: Cpu,
      title: 'Linux ARM64 (aarch64)',
      subtitle: 'Cloud & Edge - Best Performance',
      color: 'green',
      platforms: [
        { name: 'AWS Graviton', version: '2, 3, 4' },
        { name: 'Ampere Altra', version: 'All versions' },
        { name: 'Oracle Cloud ARM', version: 'A1 instances' },
        { name: 'Azure ARM64 VMs', version: 'Dpsv5, Epsv5' },
        { name: 'Google Cloud Tau T2A', version: 'All instances' },
        { name: 'Raspberry Pi', version: '4, 5 (64-bit OS)' },
      ],
    },
    {
      icon: Monitor,
      title: 'macOS',
      subtitle: 'Development & Research',
      color: 'purple',
      platforms: [
        { name: 'macOS 12 Monterey', version: 'Apple Silicon (M1/M2/M3/M4)' },
        { name: 'macOS 13 Ventura', version: 'Apple Silicon (M1/M2/M3/M4)' },
        { name: 'macOS 14 Sonoma', version: 'Apple Silicon (M1/M2/M3/M4)' },
        { name: 'macOS 15 Sequoia', version: 'Apple Silicon (M1/M2/M3/M4)' },
        { name: 'macOS 12+', version: 'Intel x86_64' },
      ],
    },
    {
      icon: Globe,
      title: 'Windows',
      subtitle: 'Via WSL2',
      color: 'blue',
      platforms: [
        { name: 'Windows 10', version: 'Build 19041+ with WSL2' },
        { name: 'Windows 11', version: 'All versions with WSL2' },
      ],
    },
    {
      icon: Cloud,
      title: 'Cloud Platforms',
      subtitle: 'All Major Providers',
      color: 'orange',
      platforms: [
        { name: 'AWS', version: 'EC2 (x86_64 & Graviton)' },
        { name: 'Google Cloud', version: 'Compute Engine, Tau T2A' },
        { name: 'Microsoft Azure', version: 'VMs (x86_64 & ARM64)' },
        { name: 'Oracle Cloud', version: 'Compute (x86_64 & ARM)' },
        { name: 'DigitalOcean', version: 'Droplets' },
        { name: 'Linode', version: 'Linodes' },
        { name: 'Vultr', version: 'Cloud Compute' },
      ],
    },
  ];

  const notSupported = [
    { platform: 'Windows Native (without WSL2)', reason: 'Engine requires Linux/Unix environment' },
    { platform: 'CentOS 6 / RHEL 6', reason: 'Kernel version too old' },
    { platform: '32-bit Operating Systems', reason: '64-bit only' },
    { platform: 'FreeBSD / OpenBSD', reason: 'Not supported by compiler toolchain' },
  ];

  const downloadPackages = [
    { name: 'Linux x86_64 Universal', size: '19 MB', useFor: 'All Linux servers (Intel/AMD)' },
    { name: 'Linux ARM64', size: '63 MB', useFor: 'AWS Graviton, ARM servers' },
    { name: 'macOS Apple Silicon', size: '40 MB', useFor: 'M1/M2/M3/M4 Macs' },
    { name: 'macOS Intel', size: '44 MB', useFor: 'Intel Macs' },
  ];

  const coloProviders = ['NYSE', 'NASDAQ', 'CME', 'SGX', 'HKEX', 'JPX', 'LSE', 'EUREX'];

  return (
    <section id="platforms" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
            <Server className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Universal Compatibility</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Runs <span className="text-gradient">Everywhere</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            ASTRA supports virtually all modern server and development platforms.
            Linux, macOS, Windows (WSL2), cloud, on-premise, colocation — we've got you covered.
          </p>
        </div>

        {/* Quick Summary Bar */}
        <div className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-green-500/10 border border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-cyan-400">15+</div>
              <div className="text-xs text-muted-foreground">Linux Distros</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">ARM64</div>
              <div className="text-xs text-muted-foreground">Graviton Ready</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-400">macOS</div>
              <div className="text-xs text-muted-foreground">M1-M4 Native</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">7+</div>
              <div className="text-xs text-muted-foreground">Cloud Providers</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-400">8+</div>
              <div className="text-xs text-muted-foreground">Exchange Colos</div>
            </div>
          </div>
        </div>

        {/* Platform Cards */}
        <div
          ref={cardsRef}
          className={`space-y-4 mb-12 transition-all duration-700 ${
            cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {platformCategories.map((category, index) => (
            <PlatformCard
              key={index}
              icon={category.icon}
              title={category.title}
              subtitle={category.subtitle}
              platforms={category.platforms}
              color={category.color}
              isExpanded={expandedCard === index}
              onToggle={() => setExpandedCard(expandedCard === index ? -1 : index)}
            />
          ))}
        </div>

        {/* Colocation Support */}
        <div className="mb-12 p-6 rounded-2xl glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-cyan-400" />
            <h3 className="text-xl font-bold text-white">Exchange Colocation Ready</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            ASTRA is verified for deployment in major exchange colocation facilities worldwide.
          </p>
          <div className="flex flex-wrap gap-2">
            {coloProviders.map((colo, index) => (
              <span
                key={index}
                className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium"
              >
                {colo}
              </span>
            ))}
          </div>
        </div>

        {/* Download Packages */}
        <div className="mb-12 p-6 rounded-2xl glass-card">
          <h3 className="text-xl font-bold text-white mb-4">Download Packages</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-white font-medium">Package</th>
                  <th className="text-left py-2 text-white font-medium">Size</th>
                  <th className="text-left py-2 text-white font-medium">Use For</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {downloadPackages.map((pkg, index) => (
                  <tr key={index} className="border-b border-white/5">
                    <td className="py-2 text-cyan-400 font-medium">{pkg.name}</td>
                    <td className="py-2">{pkg.size}</td>
                    <td className="py-2">{pkg.useFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Not Supported */}
        <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" />
            Not Supported
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-white font-medium">Platform</th>
                  <th className="text-left py-2 text-white font-medium">Reason</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {notSupported.map((item, index) => (
                  <tr key={index} className="border-b border-white/5">
                    <td className="py-2 text-red-400">{item.platform}</td>
                    <td className="py-2">{item.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformSupport;
